import { DecimalPipe } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { documentKind, DocumentKind } from '@app/features/documents/document-file-name';
import {
  orientationOfPage,
  turn,
  TurnDirection,
} from '@app/features/documents/document-orientation';
import {
  DocumentOrientationService,
  OrientationSaveResult,
} from '@app/features/documents/document-orientation.service';
import { DocumentService } from '@app/features/documents/document-service';
import { CdTDocument, Orientation, PageOrientations } from '@app/features/documents/document.model';
import { PdfPageView } from '@app/features/documents/pdf-page-view/pdf-page-view';
import { PdfFile, PdfService } from '@app/features/documents/pdf.service';
import {
  LucideMinus,
  LucidePlus,
  LucideRotateCcw,
  LucideRotateCw,
  LucideTriangleAlert,
} from '@lucide/angular';
import { Subscription } from 'rxjs';

/** Où en est l'affichage du document. */
export type ViewerStatus = 'loading' | 'ready' | 'error';

/**
 * Message qui dit pourquoi un document ne s'est pas chargé. L'erreur est celle que relaie
 * `HttpErrorInterceptor` : un objet portant le `status` HTTP, ou une `Error` quand le serveur
 * n'a pas répondu.
 */
export function loadingErrorMessage(error: unknown): string {
  const status = (error as { status?: number } | null)?.status;
  switch (status) {
    case 404:
      return 'Ce document est introuvable : il a peut-être été supprimé.';
    case 401:
    case 403:
      return "Vous n'avez pas les droits nécessaires pour consulter ce document.";
    case undefined:
    case 0:
      return 'Le serveur ne répond pas. Vérifiez votre connexion, puis réessayez.';
    default:
      return `Le serveur a renvoyé une erreur (${status}). Réessayez dans un instant.`;
  }
}

/** Dessine une image dans un canvas, tournée d'un nombre de degrés dans le sens horaire. */
export function drawRotatedImage(
  canvas: HTMLCanvasElement,
  image: HTMLImageElement,
  rotation: Orientation,
): void {
  const width = image.naturalWidth;
  const height = image.naturalHeight;
  const quarter = rotation % 180 !== 0;
  canvas.width = quarter ? height : width;
  canvas.height = quarter ? width : height;
  const context = canvas.getContext('2d');
  if (!context) {
    return;
  }
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate((rotation * Math.PI) / 180);
  context.drawImage(image, -width / 2, -height / 2);
}

/** Hauteur maximale d'une image à 100 %, en part de la fenêtre. */
const imageMaxHeightRatio = 0.75;

@Component({
  selector: 'app-document-content-viewer',
  imports: [
    DecimalPipe,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    PdfPageView,
    LucidePlus,
    LucideMinus,
    LucideRotateCw,
    LucideRotateCcw,
    LucideTriangleAlert,
  ],
  templateUrl: './document-content-viewer.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./document-content-viewer.scss'],
})
export class DocumentContentViewer {
  readonly document = input.required<CdTDocument>();
  /** Émis avec le fichier reçu, pour que le parent puisse le proposer au téléchargement. */
  readonly blobLoaded = output<Blob>();
  /** Émis quand l'utilisateur demande le téléchargement d'un document non affichable. */
  readonly downloadRequested = output<void>();
  /** Émis quand l'utilisateur demande le fichier d'origine, faute de pouvoir en lire une page. */
  readonly originalRequested = output<void>();

  readonly status = signal<ViewerStatus>('loading');
  readonly kind = signal<DocumentKind | null>(null);
  readonly errorMessage = signal<string | null>(null);
  readonly zoom = signal(1);
  readonly isDragging = signal(false);
  readonly image = signal<HTMLImageElement | null>(null);
  readonly pdf = signal<PdfFile | null>(null);
  readonly pages = computed(() =>
    Array.from({ length: this.pdf()?.pageCount ?? 0 }, (_, index) => index + 1),
  );
  /** Largeur utile du panneau, en pixels CSS. */
  readonly availableWidth = signal(0);

  /**
   * Seul un document déposé se tourne ; le PDF du contrat généré, que l'API ne conserve pas, n'a
   * pas d'identifiant (cadrage 2026-018, hors périmètre).
   */
  readonly rotatable = computed(() => !!this.document().id);

  /**
   * Orientation affichée de chaque page : celle que l'API renvoie, puis celle des gestes — y
   * compris ceux d'une consultation précédente dont l'enregistrement n'a pas encore abouti.
   */
  readonly orientations = linkedSignal<CdTDocument, PageOrientations>({
    source: this.document,
    computation: (document) => ({
      ...(document.orientations ?? {}),
      ...this.orientationService.pendingOrientations(document),
    }),
  });

  /** Pages tournées dont l'orientation n'a pas pu être enregistrée. */
  readonly unsavedPages = linkedSignal<CdTDocument, number[]>({
    source: this.document,
    computation: () => [],
  });

  readonly minZoom = 0.5;
  readonly maxZoom = 3;
  readonly zoomStep = 0.25;

  private lastPointerX = 0;
  private lastPointerY = 0;
  private activePointerId?: number;
  private objectUrl?: string;
  private loading?: Subscription;
  private generation = 0;
  private readonly documentService = inject(DocumentService);
  private readonly orientationService = inject(DocumentOrientationService);
  private readonly pdfService = inject(PdfService);

  readonly previewWrapper = viewChild<ElementRef<HTMLDivElement>>('previewWrapper');
  private readonly imageCanvas = viewChild<ElementRef<HTMLCanvasElement>>('imageCanvas');

  constructor() {
    effect(() => {
      const document = this.document();
      untracked(() => this.load(document));
    });

    effect(() => {
      const image = this.image();
      const canvas = this.imageCanvas()?.nativeElement;
      const rotation = this.orientationOf(1);
      if (image && canvas) {
        drawRotatedImage(canvas, image, rotation);
      }
    });

    effect(() => {
      const image = this.image();
      const canvas = this.imageCanvas()?.nativeElement;
      if (image && canvas) {
        this.sizeImage(canvas, image, this.orientationOf(1), this.zoom(), this.availableWidth());
      }
    });

    effect((onCleanup) => {
      const wrapper = this.previewWrapper()?.nativeElement;
      if (!wrapper) {
        return;
      }
      const measure = () => this.availableWidth.set(Math.max(wrapper.clientWidth - 16, 0));
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(wrapper);
      onCleanup(() => observer.disconnect());
    });

    // La file des enregistrements survit à la visionneuse ; seul l'avertissement en dépend.
    this.orientationService.results$
      .pipe(takeUntilDestroyed())
      .subscribe((result) => this.onSaved(result));

    inject(DestroyRef).onDestroy(() => {
      this.loading?.unsubscribe();
      // Un PDF encore en cours d'ouverture ne sera plus affiché : il se refermera à son arrivée.
      this.generation++;
      this.releaseContent();
    });
  }

  /** Relance le chargement du document après un échec. */
  reload(): void {
    this.load(this.document());
  }

  orientationOf(page: number): Orientation {
    return orientationOfPage(this.orientations(), page);
  }

  /**
   * Tourne une page d'un quart de tour et l'enregistre aussitôt (RG-document-rotation-quart-
   * de-tour, RG-document-orientation-memorisee). L'affichage tourne sans attendre l'API.
   */
  rotate(page: number, direction: TurnDirection): void {
    if (!this.rotatable()) {
      return;
    }
    const degrees = turn(this.orientationOf(page), direction);
    this.orientations.update((orientations) => ({ ...orientations, [String(page)]: degrees }));
    this.orientationService.save(this.document(), page, degrees);
  }

  /** Avertit qu'une page affichée tournée ne le restera pas, ou retire l'avertissement. */
  private onSaved({ document, page, saved }: OrientationSaveResult): void {
    if (document.id !== this.document().id) {
      return;
    }
    this.unsavedPages.update((pages) => {
      const others = pages.filter((p) => p !== page);
      return saved ? others : [...others, page].sort((a, b) => a - b);
    });
  }

  private load(document: CdTDocument): void {
    this.loading?.unsubscribe();
    this.releaseContent();
    const generation = ++this.generation;
    this.zoom.set(1);
    this.errorMessage.set(null);
    this.kind.set(null);
    this.status.set('loading');
    this.loading = this.documentService.fetchBlob(document).subscribe({
      next: (blob) => {
        this.showBlob(document, blob, generation);
        this.blobLoaded.emit(blob);
      },
      error: (error: unknown) => this.fail(loadingErrorMessage(error)),
    });
  }

  private fail(message: string): void {
    this.errorMessage.set(message);
    this.status.set('error');
  }

  private showBlob(document: CdTDocument, blob: Blob, generation: number): void {
    const kind = documentKind(document.mimeType || blob.type, document.name);
    this.kind.set(kind);
    this.status.set('ready');
    if (kind === 'image') {
      this.showImage(blob, generation);
    } else if (kind === 'pdf') {
      this.showPdf(blob, generation);
    }
  }

  private showImage(blob: Blob, generation: number): void {
    this.objectUrl = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      if (generation === this.generation) {
        this.image.set(image);
      }
    };
    // Une image que le navigateur ne sait pas décoder n'est pas affichable.
    image.onerror = () => {
      if (generation === this.generation) {
        this.kind.set('unsupported');
      }
    };
    image.src = this.objectUrl;
  }

  private showPdf(blob: Blob, generation: number): void {
    this.pdfService.open(blob).then(
      (file) => {
        if (generation === this.generation) {
          this.pdf.set(file);
        } else {
          file.destroy();
        }
      },
      (error: unknown) => {
        console.error('PDF illisible', error);
        if (generation === this.generation) {
          this.fail(
            'Ce PDF n’a pas pu être lu : il est peut-être endommagé. Téléchargez-le pour l’ouvrir avec une autre application.',
          );
        }
      },
    );
  }

  /** Taille d'affichage d'une image : entière dans le panneau à 100 %, puis selon le zoom. */
  private sizeImage(
    canvas: HTMLCanvasElement,
    image: HTMLImageElement,
    rotation: Orientation,
    zoom: number,
    availableWidth: number,
  ): void {
    const quarter = rotation % 180 !== 0;
    const width = quarter ? image.naturalHeight : image.naturalWidth;
    const height = quarter ? image.naturalWidth : image.naturalHeight;
    const maxHeight = window.innerHeight * imageMaxHeightRatio;
    const fit = Math.min(1, availableWidth > 0 ? availableWidth / width : 1, maxHeight / height);
    canvas.style.width = `${width * fit * zoom}px`;
    canvas.style.height = `${height * fit * zoom}px`;
  }

  private releaseContent(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = undefined;
    }
    this.image.set(null);
    this.pdf()?.destroy();
    this.pdf.set(null);
  }

  zoomIn(): void {
    this.zoom.update((zoom) => Math.min(this.maxZoom, zoom + this.zoomStep));
  }

  zoomOut(): void {
    this.zoom.update((zoom) => Math.max(this.minZoom, zoom - this.zoomStep));
  }

  resetZoom(): void {
    this.zoom.set(1);
  }

  startDrag(event: PointerEvent): void {
    const wrapper = this.previewWrapper()?.nativeElement;
    if (event.button !== 0 || !wrapper || (event.target as HTMLElement).closest('button')) {
      return;
    }
    this.isDragging.set(true);
    this.lastPointerX = event.clientX;
    this.lastPointerY = event.clientY;
    this.activePointerId = event.pointerId;
    wrapper.setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  drag(event: PointerEvent): void {
    const wrapper = this.previewWrapper()?.nativeElement;
    if (!this.isDragging() || !wrapper) {
      return;
    }
    wrapper.scrollLeft -= event.clientX - this.lastPointerX;
    wrapper.scrollTop -= event.clientY - this.lastPointerY;
    this.lastPointerX = event.clientX;
    this.lastPointerY = event.clientY;
  }

  endDrag(): void {
    if (!this.isDragging()) {
      return;
    }
    this.isDragging.set(false);
    const wrapper = this.previewWrapper()?.nativeElement;
    if (wrapper && this.activePointerId != null) {
      try {
        wrapper.releasePointerCapture(this.activePointerId);
      } catch {
        // la capture a déjà été relâchée
      }
    }
    this.activePointerId = undefined;
  }
}
